<template>
  <Loading v-if="!state.initialLoad" />

  <a
    data-cy="ptp-widget"
    v-if="state.initialLoad && !isWidget"
    target="_blank"
    :href="getButtonLocation()"
    :style="{ color: contrastingCustomColour, 'background-color': state.widget.customColour }"
    class="cursor-pointer hover:opacity-75 inline-block text-center ease-in duration-300 focus:outline-none"
    :class="`${getButtonRadiusClass} ${getButtonFontSizeClass}`"
    >{{ state.widget.customButtonText }}</a
  >

  <Submitting
    v-if="!isPaused && state.initialLoad && isWidget && state.isSubmitting && state.planId"
    :customColour="state.widget.customColour"
    :planId="state.planId"
    :isDone="state.isDone"
  />

  <InactiveWidget v-if="state.initialLoad && isWidget && (isPaused || isRateLimited)" />

  <div
    data-cy="ptp-widget"
    v-if="!isPaused && !isRateLimited && state.initialLoad && isWidget && !state.isSubmitting"
    class="text-sm bg-white border-t-8 p-4 flex flex-col md:flex-row flex-wrap"
    :style="{ 'border-color': state.widget.customColour }"
  >
    <div class="top md:w-full md:flex">
      <header class="text-center mb-4 md:text-left md:w-1/2 md:border-r md:pr-4 md:mb-0">
        <h1 class="text-2xl font-bold mt-2">{{ state.widget.title }}</h1>
        <p class="mt-2">{{ state.widget.introduction }}</p>
      </header>

      <section class="mb-4 md:mb-0 md:w-1/2 md:pl-4">
        <div class="flex items-center mb-2">
          <h3 class="font-bold">Your details</h3>
          <span class="h-0 border-t-2 border-grey flex-1 ml-8"></span>
        </div>

        <div class="flex items-center mb-1">
          <label class="w-40 mr-4">First Name</label>
          <div class="w-full">
            <input
              data-cy="widget-first-name-input"
              v-model="state.name"
              class="appearance-none border rounded w-full py-1 px-3 text-gray-700 bg-lightGrey"
              :class="state.errors.name && 'border-red-500'"
              @input="validateField('name')"
              @blur="validateField('name')"
            />
            <span v-if="state.errors.name" class="block text-xs text-red-500 py-1">{{
              state.errors.name
            }}</span>
          </div>
        </div>

        <div class="flex items-center mb-1">
          <label class="w-40 mr-4">Last Name</label>
          <div class="w-full">
            <input
              data-cy="widget-last-name-input"
              v-model="state.lastName"
              class="appearance-none border rounded w-full py-1 px-3 text-gray-700 bg-lightGrey"
              :class="state.errors.name && 'border-red-500'"
              @input="validateField('lastName')"
              @blur="validateField('lastName')"
            />
            <span v-if="state.errors.lastName" class="block text-xs text-red-500 py-1">{{
              state.errors.lastName
            }}</span>
          </div>
        </div>

        <div class="flex items-center mb-1">
          <label class="w-40 mr-4">Email Address</label>
          <div class="w-full">
            <input
              data-cy="widget-email-address-input"
              v-model="state.email"
              class="appearance-none border rounded w-full py-1 px-3 text-gray-700 bg-lightGrey"
              :class="state.errors.email && 'border-red-500'"
              @blur="validateField('email')"
            />
            <span v-if="state.errors.email" class="block text-xs text-red-500 py-1">{{
              state.errors.email
            }}</span>
          </div>
        </div>
      </section>
    </div>

    <div class="left md:w-1/2 md:border-r md:pr-4">
      <section class="mb-4">
        <div class="flex items-center mb-1">
          <h3 class="font-bold">Your Journey</h3>
          <span class="h-0 border-t-2 border-grey flex-1 ml-8"></span>
        </div>

        <div class="flex items-center mb-1 ptp__autocomplete">
          <label class="w-40 mr-4">From</label>
          <div class="w-full">
            <AutoComplete
              data-cy="widget-origin-input"
              :api="api"
              :class="fromError"
              @autocompleted="onAutocomplete($event, LocationType.Origin)"
              placeholder="Enter your starting point"
              :isplaces="true"
              formname="Origin"
            />
            <span v-if="state.errors.origin" class="block text-xs text-red-500 py-1">{{
              state.errors.origin
            }}</span>
          </div>
        </div>

        <div class="flex items-center mb-1 ptp__autocomplete">
          <label class="w-40 mr-4">To</label>

          <div v-if="state.widget.locations">
            <BrandSelect
              v-model="state.destinationId"
              class="w-full mb-0"
              data-cy="location-select"
            >
              <option v-for="(location, index) in state.widget.locations" :value="location.id">
                {{ location.name ? location.name : location.address }}
              </option>
            </BrandSelect>
          </div>

          <div class="w-full" v-else>
            <AutoComplete
              :api="api"
              :class="toError"
              @autocompleted="onAutocomplete($event, LocationType.Destination)"
              placeholder="Enter your destination"
              :isplaces="true"
              formname="Destination"
            />
            <span v-if="state.errors.destination" class="block text-xs text-red-500 py-1">{{
              state.errors.destination
            }}</span>
            <span v-if="state.errors.journey" class="block text-xs text-red-500 py-1">{{
              state.errors.journey
            }}</span>
          </div>
        </div>
      </section>

      <section class="border-t-2 pt-4">
        <div class="flex justify-between items-center mb-8">
          <label for="for-return-journey">Is this a return journey?</label>
          <ToggleSwitch
            class="toggle-return"
            size="lg"
            :custom-colour="state.widget.customColour"
            unique-id="for-return-journey"
            :initial-state="state.isReturn"
            @toggled="state.isReturn = !state.isReturn"
          />
        </div>
        <div class="flex gap-x-8 gap-y-8 md:flex-row">
          <div class="flex-1" style="min-width: 155px">
            <div class="flex justify-between">
              <p>Outbound</p>
            </div>
            <DatePicker
              v-model:value="state.outboundDate"
              :disabledDate="disabledOutboundDate"
              @change="outboundChange"
              class="w-full"
            >
              <span class="mb-1 block border rounded w-full py-1 px-3 text-gray-700 bg-lightGrey">{{
                state.outboundDate.format('DD/MM/YYYY')
              }}</span>
            </DatePicker>
            <BrandSelect v-model="state.outboundArriveBy" class="mb-1 py-1">
              <option :value="false">Leave At</option>
              <option :value="true">Arrive By</option>
            </BrandSelect>
            <TimePicker
              v-model:value="state.outboundTimeUtc"
              :disabledHours="disabledOutboundHours"
              :disabledMinutes="disabledOutboundMinutes"
              :default-value="state.outboundTimeUtc"
              format="HH:mm"
            />
          </div>
          <div class="flex-1 relative" style="min-width: 155px">
            <div
              class="absolute left-0 right-0 top-0 bottom-0 bg-white opacity-75 z-10"
              :class="{ [state.isReturn ? 'hidden' : 'block']: state.isReturn }"
            ></div>
            <div class="flex justify-between">
              <p>Return</p>
            </div>
            <DatePicker
              v-model:value="state.returnDate"
              disabled="disabled"
              :disabledDate="disabledReturnDate"
              class="w-full"
            >
              <span class="mb-1 block border rounded w-full py-1 px-3 text-gray-700 bg-lightGrey">{{
                state.returnDate.format('DD/MM/YYYY')
              }}</span>
            </DatePicker>
            <BrandSelect v-model="state.returnArriveBy" class="mb-1">
              <option :value="false">Leave At</option>
              <option :value="true">Return By</option>
            </BrandSelect>
            <TimePicker v-model:value="state.returnTimeUtc" format="HH:mm" />
          </div>
        </div>
      </section>
    </div>

    <div class="right md:w-1/2 md:pl-4 md:flex md:flex-col">
      <section class="mt-4 sm:flex sm:flex-col sm:h-full">
        <div>
          <div class="flex items-center mb-4">
            <h3 class="font-bold">and finally</h3>
            <span class="h-0 border-t-2 border-grey flex-1 ml-8"></span>
          </div>
          <BrandSelect
            v-model="state.previousTravelMode"
            label="How do you or would you make this journey?"
            class="mb-2"
          >
            <option value="" hidden>Please select</option>
            <option v-for="mode in listOfTravelModes" :value="mode.value">{{ mode.title }}</option>
          </BrandSelect>
        </div>
        <div class="md:mt-auto md:mb-auto mx-auto text-center text-xl">
          <BrandButton
            data-cy="widget-submit-button"
            class="mt-4 mb-4"
            :button-color="state.widget.customColour"
            @click="submitWidget"
            size="md"
            >{{ state.widget.customButtonText }}</BrandButton
          >
        </div>
      </section>

      <footer class="text-center mt-8 pb-8 sm:order-6 sm:mt-auto sm:pb-0">
        <p class="text-xs mr-4">
          By using this service you agree to our
          <a class="link" href="https://liftshare.com/uk/terms-and-conditions">Terms of Use</a> and
          <a class="link" href="https://liftshare.com/uk/privacy-policy">Privacy Policy</a>. Your
          data will be used to provide you with transport options, and be accesible by Liftshare to
          assist you in using the service.
        </p>
        <svg style="max-width: 100px" class="mx-auto mt-8" viewBox="0 0 141.448 67.077">
          <g id="Group_2345" data-name="Group 2345" transform="translate(-520 -347)">
            <path
              id="Path_1"
              data-name="Path 1"
              d="M420.49,20.255l6.429-6.429,6.43,6.429L452.637.967l19.288,19.288L488,4.182l6.429,6.429-22.5,22.5L452.638,13.826,433.349,33.114,420.49,20.255Z"
              transform="translate(167.019 346.033)"
              fill="#01d2c5"
              fill-rule="evenodd"
            />
            <path
              id="Path_2"
              data-name="Path 2"
              d="M22.993.967,42.282,20.255,61.57.967,74.429,13.826,68,20.255,61.57,13.826,42.282,33.114,22.993,13.826,6.919,29.9.49,23.47l22.5-22.5Z"
              transform="translate(519.51 346.033)"
              fill="#f163c1"
              fill-rule="evenodd"
            />
            <path
              id="Path_3"
              data-name="Path 3"
              d="M29.6,274.2a6.232,6.232,0,0,1,1.8-4.51,6,6,0,0,1,2-1.267,7.267,7.267,0,0,1,5.067,0,6,6,0,0,1,2,1.267,6.667,6.667,0,0,1,1.318,7.043,5.892,5.892,0,0,1-1.318,1.951,5.683,5.683,0,0,1-2,1.292,7.265,7.265,0,0,1-5.067,0,5.679,5.679,0,0,1-2-1.292,5.893,5.893,0,0,1-1.317-1.951A6.273,6.273,0,0,1,29.6,274.2Zm2.128,0a4.732,4.732,0,0,0,.3,1.723,3.538,3.538,0,0,0,.836,1.393,3.889,3.889,0,0,0,1.343.937,4.188,4.188,0,0,0,1.723.355,4.4,4.4,0,0,0,1.748-.355A4.039,4.039,0,0,0,39,277.312a3.923,3.923,0,0,0,.862-1.394,5.339,5.339,0,0,0,.279-1.723,5.494,5.494,0,0,0-.279-1.748A4.153,4.153,0,0,0,39,271.054a4.455,4.455,0,0,0-1.318-.937,4.389,4.389,0,0,0-1.748-.329,4.177,4.177,0,0,0-1.723.33,4.261,4.261,0,0,0-1.343.937,3.727,3.727,0,0,0-.836,1.394,4.872,4.872,0,0,0-.3,1.748ZM44.3,260.97h1.976v9.02h.051a5.723,5.723,0,0,1,1.9-1.495,5.193,5.193,0,0,1,2.356-.532,6.967,6.967,0,0,1,2.559.456,5.894,5.894,0,0,1,1.951,1.318,6.152,6.152,0,0,1,1.267,1.976,6.987,6.987,0,0,1,0,4.966,6.09,6.09,0,0,1-1.242,1.976,5.5,5.5,0,0,1-1.9,1.292,5.853,5.853,0,0,1-5.118-.228,19.907,19.907,0,0,1-1.824-1.115v1.52H44.3ZM46.277,274.2a4.735,4.735,0,0,0,.3,1.723,3.542,3.542,0,0,0,.836,1.393,3.889,3.889,0,0,0,1.343.937,4.188,4.188,0,0,0,1.723.355,4.4,4.4,0,0,0,1.748-.355,4.04,4.04,0,0,0,1.317-.937,3.919,3.919,0,0,0,.862-1.394,5.339,5.339,0,0,0,.279-1.723,5.5,5.5,0,0,0-.279-1.748,4.153,4.153,0,0,0-.862-1.394,4.457,4.457,0,0,0-1.317-.937,4.389,4.389,0,0,0-1.748-.329,4.177,4.177,0,0,0-1.723.33,4.261,4.261,0,0,0-1.343.937,3.731,3.731,0,0,0-.836,1.394,4.875,4.875,0,0,0-.3,1.748Zm13.065-5.928h1.976v11.857H59.342Zm-.456-4.637a1.42,1.42,0,0,1,.431-1.013,1.378,1.378,0,0,1,1.013-.431,1.435,1.435,0,0,1,1.039.431,1.507,1.507,0,0,1,.405,1.013,1.445,1.445,0,1,1-2.888,0Zm6.031-2.66h1.976v19.154H64.916Zm5.6,7.3h1.976v11.857H70.515Zm-.456-4.637a1.42,1.42,0,0,1,.431-1.013,1.377,1.377,0,0,1,1.013-.431,1.435,1.435,0,0,1,1.039.431,1.507,1.507,0,0,1,.405,1.013,1.445,1.445,0,1,1-2.888,0Zm12.973,6.309h-3.5v5.929c0,.38,0,.76.025,1.064a2.158,2.158,0,0,0,.2.862,1.325,1.325,0,0,0,.532.583,1.877,1.877,0,0,0,1.013.228c.279,0,1.824.025,1.824.025v1.8H80.827a4.469,4.469,0,0,1-1.7-.279,2.21,2.21,0,0,1-.988-.786,3,3,0,0,1-.456-1.267,10.623,10.623,0,0,1-.127-1.647v-6.511H74.975v-1.672h2.584V264.9h1.976v3.37h3.5v1.672Zm5.9,16.316H86.65l2.306-6.055L84.04,268.267h2.306l3.674,9.3h.051l3.522-9.3h2.154L88.93,286.255Zm43.32,0h-2.28l2.306-6.055-4.915-11.933h2.306l3.674,9.3h.051l3.522-9.3h2.154Zm-20.4-6.131H109.9L106.527,271h-.051l-2.99,9.121h-2.027l-3.851-11.857h2.179l2.711,9.121h.051l3.066-9.121h2.078l3.116,9.121h.051l2.686-9.121h2.128Zm4.833-10.337a5.647,5.647,0,0,1,2.179-1.394,7.946,7.946,0,0,1,2.534-.431,5.009,5.009,0,0,1,3.8,1.242,5.307,5.307,0,0,1,1.165,3.775v5.092c0,.33.025,2.052.025,2.052h-1.849s-.025-1.267-.025-1.52a14.222,14.222,0,0,1-1.6,1.241,4.64,4.64,0,0,1-2.356.583,5.978,5.978,0,0,1-1.7-.228,3.776,3.776,0,0,1-1.394-.684,3.062,3.062,0,0,1-.963-1.089,3.208,3.208,0,0,1-.355-1.52,3.287,3.287,0,0,1,.76-2.306,4.687,4.687,0,0,1,1.9-1.242,9,9,0,0,1,2.432-.507c.862-.076,1.647-.1,2.382-.1h.76V272.4a2.43,2.43,0,0,0-.76-1.951,3.337,3.337,0,0,0-2.179-.659,5.925,5.925,0,0,0-1.875.3,4.923,4.923,0,0,0-1.647.988l-1.242-1.292Zm5.726,4.636a7.068,7.068,0,0,0-3.015.532,1.82,1.82,0,0,0-1.115,1.748,1.691,1.691,0,0,0,.76,1.571,3.241,3.241,0,0,0,1.85.481,3.538,3.538,0,0,0,1.52-.3,2.726,2.726,0,0,0,1.064-.76,3.633,3.633,0,0,0,.659-1.14,4.848,4.848,0,0,0,.253-1.368v-.76Zm18.534,2.584a4.659,4.659,0,0,0,1.292,1.165,3.307,3.307,0,0,0,1.748.431,4.087,4.087,0,0,0,.937-.1,3.037,3.037,0,0,0,.836-.33,1.914,1.914,0,0,0,.608-.583,1.39,1.39,0,0,0,.228-.811,1.441,1.441,0,0,0-.2-.786,2.362,2.362,0,0,0-.583-.532,3.392,3.392,0,0,0-.811-.329q-.468-.118-.938-.228c-.557-.127-1.089-.253-1.6-.405a4.851,4.851,0,0,1-1.318-.608,2.692,2.692,0,0,1-.912-.937,2.9,2.9,0,0,1-.355-1.495,2.942,2.942,0,0,1,.38-1.52,3.2,3.2,0,0,1,1.013-1.089,5.2,5.2,0,0,1,1.419-.659,5.828,5.828,0,0,1,1.621-.228,5.7,5.7,0,0,1,2.356.507,3.624,3.624,0,0,1,1.672,1.647l-1.647,1.089a3.227,3.227,0,0,0-.988-1.039,3.252,3.252,0,0,0-2.331-.279,3.411,3.411,0,0,0-.76.3,1.463,1.463,0,0,0-.532.507,1.262,1.262,0,0,0-.228.735.84.84,0,0,0,.253.633,1.93,1.93,0,0,0,.659.481,5.956,5.956,0,0,0,.963.355c.355.1.735.177,1.115.253a12.866,12.866,0,0,1,1.495.38,4.44,4.44,0,0,1,1.241.659,2.945,2.945,0,0,1,.836,1.013,3.846,3.846,0,0,1-.1,3.218,3.3,3.3,0,0,1-1.064,1.14,4.546,4.546,0,0,1-1.52.659,8.228,8.228,0,0,1-1.748.2,6.317,6.317,0,0,1-2.61-.481,4.7,4.7,0,0,1-2-1.723ZM10.485,280.124l.026-11.941h1.812v1.761a8.773,8.773,0,0,1,.638-.714,8.53,8.53,0,0,1,.867-.663,4.74,4.74,0,0,1,1.148-.51,4.857,4.857,0,0,1,1.378-.179,5.038,5.038,0,0,1,1.633.23,5.182,5.182,0,0,1,1.072.587,3.711,3.711,0,0,1,.689.74c.153.281.332.536.332.536a13.44,13.44,0,0,1,1.531-1.429,3.95,3.95,0,0,1,2.3-.663,6.521,6.521,0,0,1,2.143.332,3.588,3.588,0,0,1,1.4,1,3.982,3.982,0,0,1,.766,1.582,8.911,8.911,0,0,1,.23,2.092v7.246h-1.99V272.98a6.153,6.153,0,0,0-.128-1.2,2.577,2.577,0,0,0-.459-1.046,2.324,2.324,0,0,0-.893-.74,3.31,3.31,0,0,0-1.429-.281,2.878,2.878,0,0,0-2.169.97,3.412,3.412,0,0,0-.663,1.1,3.787,3.787,0,0,0-.255,1.454v6.889h-1.99v-6.762a4.5,4.5,0,0,0-.536-2.577,2.454,2.454,0,0,0-2.194-1.072,3.091,3.091,0,0,0-2.373,1.276,3.806,3.806,0,0,0-.663,1.454,7.021,7.021,0,0,0-.23,1.863v5.817Z"
              transform="translate(511.121 127.822)"
            />
          </g>
        </svg>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, reactive } from 'vue';
import moment, { Moment } from 'moment';
import { contrastingColour } from '@/plugin/functions';
import { useApi } from '@/mw-components/Api';
import AutoComplete from '@/mw-components/components/Autocomplete/Autocomplete.vue';
import BrandButton from '@/mw-components/components/BrandButton/BrandButton.vue';
import BrandSelect from '@/mw-components/components/BrandSelect/BrandSelect.vue';
import { GmapsLocation, Location } from '@/mw-components/types/location';
import Loading from '@/mw-components/components/Loading/Loading.vue';
import { LocationType } from '@/mw-components/enums/location';
import ToggleSwitch from '@/mw-components/components/ToggleSwitch/ToggleSwitch.vue';
import { Widget, WidgetEmailSettings } from '@/mw-components/types/widgets';
import {
  WidgetButtonFontSize,
  WidgetButtonRadius,
  WidgetStatus,
  WidgetType,
} from '@/mw-components/enums/widgets';
import router from '../../../router';
import DatePicker from 'ant-design-vue/lib/date-picker';
import 'ant-design-vue/lib/date-picker/style/index.css';
import TimePicker from 'ant-design-vue/lib/time-picker';
import 'ant-design-vue/lib/time-picker/style/index.css';
import Submitting from './submitting.vue';
import InactiveWidget from './inactiveWidget.vue';
import { WidgetRateLimitStatus } from '@/mw-components/enums/widgets';
import { GeoRestrictionType } from '@/assets/enum';

import { listOfTravelModes } from '@/views/TravelPlan/helpers/list-of-travel-modes';

const api = useApi(import.meta.env.MODE);

const state = reactive<{
  errors: {
    origin?: string;
    destination?: string;
    name?: string;
    lastName?: string;
    email?: string;
    journey?: string;
  };
  initialLoad: boolean;
  isSubmitting: boolean;
  isDone: boolean;
  name: string;
  lastName: string;
  email: string;
  outboundDate: Moment;
  outboundTimeUtc: Moment;
  outboundArriveBy: string; // TODO: this should be boolean, what's up with Brandselect?
  returnDate: Moment;
  returnTimeUtc: Moment;
  returnArriveBy: string;
  origin?: Location;
  destination?: Location;
  destinationId?: string;
  previousTravelMode: number;
  isReturn: boolean;
  widget?: Widget;
  planId?: string;
  geoRestrictionType: GeoRestrictionType;
  geofenceLocationIds: Array<number>;
}>({
  errors: {},
  initialLoad: false,
  isSubmitting: false,
  isDone: false,
  name: '',
  lastName: '',
  email: '',
  outboundDate: moment().add(1, 'days'),
  outboundTimeUtc: moment('9:00', 'HH:mm'),
  outboundArriveBy: 'true',
  returnDate: moment().add(1, 'days'),
  returnTimeUtc: moment('17:30', 'HH:mm'),
  returnArriveBy: 'true',
  isReturn: true,
  previousTravelMode: 12,
  geoRestrictionType: GeoRestrictionType.Geofence,
  geofenceLocationIds: [],
});

const { widgetId } = router.currentRoute.value.params;

onBeforeMount(() => {
  api
    .Widget(`/${widgetId}`)
    .then((resp: { emailSettings: WidgetEmailSettings; widget: Widget }) => {
      const data = resp;
      state.initialLoad = true;
      const {
        defaultReturnBy,
        defaultReturnTime,
        defaultArriveBy,
        defaultOutboundTime,
        locations,
        geofenceLocationIds,
      } = data.widget;
      if (defaultArriveBy != null) state.outboundArriveBy = defaultArriveBy ? 'true' : 'false';
      if (defaultOutboundTime) state.outboundTimeUtc = moment(defaultOutboundTime, 'HH:mm');
      if (defaultReturnBy != null) state.returnArriveBy = defaultReturnBy ? 'true' : 'false';
      if (defaultReturnTime) state.returnTimeUtc = moment(defaultReturnTime, 'HH:mm');
      if (locations && locations.length > 0) {
        state.geoRestrictionType = GeoRestrictionType.DefinedDestinations;
        state.destination = locations[0];
        state.destinationId = locations[0].id as string;
      } else if (geofenceLocationIds) {
        state.geoRestrictionType = GeoRestrictionType.Geofence;
        state.geofenceLocationIds = geofenceLocationIds;
      } else {
        // TODO: Handle error here
        console.log('error - widget has no geographical restriction');
      }
      state.widget = data.widget;
    })
    .catch(() => {});
});

const getButtonLocation = () => {
  return window.location.href.replace('embed', 'widget');
};

const isPaused = computed<boolean>(() => {
  return (
    state.widget?.status === WidgetStatus.Draft || state.widget?.status === WidgetStatus.Paused
  );
});

const isRateLimited = computed<boolean>(() => {
  return state.widget?.rateLimitStatus === WidgetRateLimitStatus.Exceeded;
});

const isWidget = computed<boolean>(() => {
  return state.widget?.type === WidgetType.Iframe;
});

const getButtonRadiusClass = computed<string | null>(() => {
  if (!state.widget) return null;

  const { customButtonRadius: radius } = state.widget;

  switch (radius) {
    case WidgetButtonRadius.None:
      return 'rounded-none';
    case WidgetButtonRadius.Small:
      return 'rounded-sm';
    case WidgetButtonRadius.Medium:
      return 'rounded-md';
    case WidgetButtonRadius.Large:
      return 'rounded-lg';
    case WidgetButtonRadius.Full:
      return 'rounded-full';
    default:
      return null;
  }
});

const getButtonFontSizeClass = computed<string | null>(() => {
  if (!state.widget) return null;
  const { customButtonFontSize: fontSize } = state.widget;
  switch (fontSize) {
    case WidgetButtonFontSize.XSmall:
      return 'px-2 py-1 text-xs';
    case WidgetButtonFontSize.Small:
      return 'px-4 py-2 text-sm';
    case WidgetButtonFontSize.Medium:
      return 'px-6 py-2';
    case WidgetButtonFontSize.Large:
      return 'px-8 py-3';
    case WidgetButtonFontSize.XLarge:
      return 'px-12 py-4 text-2xl';
    case WidgetButtonFontSize.XXLarge:
      return 'px-12 py-4 text-3xl';
    default:
      return null;
  }
});

const contrastingCustomColour = computed<string | null>(() => {
  if (!state.widget) return null;
  return contrastingColour(state.widget.customColour);
});

const fromError = computed<string>(() => {
  if (state.errors.origin || state.errors.journey) {
    return 'w-full py-1 border-red-500';
  } else {
    return 'w-full py-1';
  }
});

const toError = computed<string>(() => {
  if (state.errors.destination || state.errors.journey) {
    return 'w-full py-1 border-red-500';
  } else {
    return 'w-full py-1';
  }
});

const outboundChange = (date: Moment) => {
  state.returnDate = date;
};

const disabledOutboundDate = (date: Moment): boolean => {
  return date.isBefore(moment(), 'day');
};

const disabledOutboundHours = (): Array<number> => {
  if (state.returnDate.isSame(state.outboundDate, 'day')) {
    const hours = [...Array(24).keys()];
    const removeHours = [...Array(state.returnTimeUtc.hour() + 1).keys()];
    return hours.filter((hour) => !removeHours.includes(hour));
  }
  return [];
};

const disabledOutboundMinutes = (hour: number): Array<number> => {
  if (state.returnTimeUtc.hour() === hour) {
    const minutes = [...Array(60).keys()];
    const removeMinutes = [...Array(state.returnTimeUtc.minute() + 1).keys()];
    return minutes.filter((min) => !removeMinutes.includes(min));
  }
  return [];
};

const hasAnyValue = (data: string) => data && data.length > 0;

const isEmail = (data: string) => {
  const regex =
    /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
  return regex.test(String(data).toLowerCase());
};

const onAutocomplete = (location: GmapsLocation, type: LocationType) => {

  if (type === LocationType.Origin) {
    if (!location) {
      state.origin = undefined;
    } else {
      state.origin = {
        address: location.Address,
        latitude: location.Latitude,
        longitude: location.Longitude,
      };

      delete state.errors.origin;
      delete state.errors.journey;
      validateJourney();
    }
  } else if (type === LocationType.Destination) {
    if (!location) {
      state.destination = undefined;
    } else {
      state.destination = {
        address: location.Address,
        latitude: location.Latitude,
        longitude: location.Longitude,
      };

      delete state.errors.destination;
      delete state.errors.journey;
      validateJourney();
    }
  } else {
  }
};

const validateJourney = async () => {
  if (state.geoRestrictionType !== GeoRestrictionType.Geofence) {
    return true;
  }

  if (!(state.origin && state.destination)) {
    return false;
  }

  const resp = await api.WidgetsValidateLocations({
    publicWidgetId: state.widget?.id,
    origin: state.origin,
    destination: state.destination,
  });

  if (!(resp.originValid || resp.destinationValid)) {
    state.errors.journey = `Either the 'From' or 'To' locations must fall within: ${resp.validLocations}`;
    return false;
  } else {
    return true;
  }
};

const validateForm = async (fields: Array<string>): Promise<boolean> => {
  let isValid = true;

  const validations = {
    name: hasAnyValue(state.name) ? true : 'Please provide your first name',
    lastName: hasAnyValue(state.lastName) ? true : 'Please provide your last name',
    email: isEmail(state.email) ? true : 'Please enter a valid email address',
    origin: state.origin ? true : 'Please type a location and select an option from the list',
    destination: state.destination
      ? true
      : 'Please type a location and select an option from the list',
  };

  fields.forEach((key) => {
    if (typeof validations[key] !== 'boolean') {
      state.errors[key] = validations[key];
      isValid = false;
    } else {
      delete state.errors[key];
    }
  });

  // Now validate the georestriction on the journey
  isValid = isValid && (await validateJourney());

  return isValid;
};

const validateField = async (field: string) => {
  const isValid = await validateForm([field]);

  if (isValid) {
    delete state.errors[field];
  }
};

const submitWidget = async () => {
  const canSubmit = await validateForm(['name', 'email', 'origin', 'destination']);

  if (canSubmit) {
    state.isSubmitting = true;

    api
      .TravelPlanWidgetSearch({
        widgetId: state.widget?.id,
        name: state.name,
        lastName: state.lastName,
        email: state.email,
        dateOfJourney: state.outboundDate.utc(),
        origin: state.origin,
        destination: state.destination,
        destinationId: state.destinationId,
        isReturn: state.isReturn,
        outboundTimeUtc: state.outboundTimeUtc.format('HH:mm'),
        outboundArriveBy: state.outboundArriveBy === 'true',
        returnTimeUtc: state.returnTimeUtc.format('HH:mm'),
        returnArriveBy: state.returnArriveBy === 'true',
        previousTravelMode: parseInt(state.previousTravelMode),
      })
      .then((resp) => {
        const { publicSearchId } = resp;
        state.planId = publicSearchId;
        state.isDone = true;
      })
      .catch(() => {});
  }
};

const disabledReturnDate = (date: moment.Moment): boolean => {
  return date.isBefore(state.outboundDate) || date.isBefore(moment(), 'day');
};
</script>
<style lang="scss">
.toggle-return div {
  width: 5rem;
}

.ptp__autocomplete div {
  width: 100%;
}
.ant-time-picker,
span.ant-time-picker {
  width: 100%;
}
.ant-time-picker .ant-time-picker-input {
  background-color: #f2f2f2;
  @apply appearance-none border rounded w-full py-1 px-3 text-gray-700;
}

.autocomplete__input {
  @apply py-1;
}

.left select,
.right select {
  @apply py-1;
}
</style>
